import React, { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { Button, HookSelectFormGroup, Skeleton } from "components";
import { useLoader } from "hooks";

import { HookSelectFormGroupProps } from "../hookSelectFormGroup";

import { ReactComponent as CancelIcon } from "static/images/cancel.svg";
import { ReactComponent as CheckDone } from "static/images/checked.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./editableFieldWithHookSelect.module.scss";

type EditableFieldWithHookSelectProps = {
	reactSelectProps: HookSelectFormGroupProps;
	defaultValue: string | undefined;
	defaultValuePlaceholder?: string;
	getSelectedValue?: Function;
	onSubmit?: Function;
	isLoading?: boolean;
	isEditable?: boolean;
	classNameViewMode?: string;
	classNameButtons?: string;
};

const EditableFieldWithHookSelect = ({
	reactSelectProps,
	defaultValue,
	defaultValuePlaceholder,
	getSelectedValue,
	onSubmit,
	isLoading = false,
	isEditable = true,
	classNameViewMode = "",
	classNameButtons = "",
}: EditableFieldWithHookSelectProps) => {
	const [initValue, setInitValue] = useState<string | undefined>();
	const [isEditMode, setIsEditMode] = useState<boolean>(false);

	const { handleSubmit } = useFormContext();
	const Loader = useLoader(isLoading);

	const selectedValue = useMemo(() => {
		if (!defaultValue) {
			return null;
		}
		reactSelectProps.options?.find(option =>
			getSelectedValue
				? getSelectedValue(option, defaultValue)
				: (option, defaultValue) => option.displayName === defaultValue,
		);
	}, [defaultValue, getSelectedValue, reactSelectProps.options]);

	const handleEditField = useCallback(
		e => {
			e.stopPropagation();
			setInitValue(initValue === undefined ? defaultValue : initValue);
			setIsEditMode(true);
		},
		[defaultValue, initValue],
	);

	const handleSaveField = useCallback(
		e => {
			e.stopPropagation();
			handleSubmit(values => {
				onSubmit?.(values[reactSelectProps.name], () => {
					setInitValue(values[reactSelectProps.name]?.displayName);
					setIsEditMode(false);
				});
			})();
		},
		[handleSubmit, onSubmit, reactSelectProps.name],
	);

	return (
		<Loader
			skeleton={<Skeleton variant="text" height="32px" />}
			component={
				isEditable && isEditMode ? (
					<div className={styles.container}>
						<HookSelectFormGroup
							{...reactSelectProps}
							defaultValue={selectedValue}
							colSelect={{ sm: 12 }}
							containerClassName={styles.field}
							hideLabel={true}
						/>
						<div className={classNameButtons}>
							<Button
								onClick={e => handleSaveField(e)}
								icon={<CheckDone />}
								btnIcon
								variant="secondary"
								size="sm"
								title="Save"
							/>
							<Button
								onClick={e => {
									e.stopPropagation();
									setIsEditMode(false);
								}}
								icon={<CancelIcon />}
								btnIcon
								variant="secondary-danger"
								size="sm"
								title="Cancel"
							/>
						</div>
					</div>
				) : (
					<>
						<span className={classNameViewMode}>
							{(initValue === undefined
								? defaultValue
								: initValue) || defaultValuePlaceholder}
						</span>
						{isEditable && (
							<Button
								onClick={e => {
									handleEditField(e);
								}}
								btnIcon
								icon={<EditIcon />}
								variant="secondary"
								size="sm"
								title="Edit"
							/>
						)}
					</>
				)
			}
		/>
	);
};

export default EditableFieldWithHookSelect;
